import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import saveAs from 'file-saver';
import { TableLazyLoadEvent } from 'primeng/table';
import { TableConfig } from 'src/app/shared/components/app-table/app-table-models';
import { dbStringToGui } from 'src/app/shared/helpers/mappers/mapping-utils';
import { formatBigNumber } from 'src/app/shared/helpers/string-utils/string-utils';
import { GlobalStateService } from 'src/app/shared/services/global-state.service';
import { SearchDTO, SearchType } from '../../models/analys-resultat';
import { AnalysresultatService } from '../analysresultat.service';

@Component({
    selector: 'app-search-result-list',
    templateUrl: './search-result-list.component.html',
    styleUrl: './search-result-list.component.scss',
    standalone: false
})
export class SearchResultListComponent implements OnInit {
  private analysresultatService = inject(AnalysresultatService);
  private destroy = inject(DestroyRef);
  protected globalStateService = inject(GlobalStateService);

  public rowData: any;
  protected currentRow!: any;
  protected initialSearchParams!: SearchDTO;
  protected searchParams!: SearchDTO;
  protected totalRecords!: number;
  protected numberOfKML!: number;
  protected searchTypes = SearchType;
  protected loading = true;
  protected countLoading = true;
  protected kmlLoading = true;

  protected tableConfig: TableConfig = {
    dataKey: 'provDataId',
    expandMode: 'single',
    multiSortMeta: [
      { field: 'provtagningsDatum', order: 1 },
      { field: 'labb', order: 1 },
      { field: 'provId', order: 1 },
    ],
    columns: [
      { field: 'toggler', header: '', isRowToggler: true, click: (row) => this.getAntibiotics(row) },
      { field: 'provId', header: 'Prov ID', sortByField: 'provId' },
      { field: 'labb', header: 'Labbkod', sortByField: 'labb' },
      { field: 'analys', header: 'Analys', sortByField: 'analys' },
      { field: 'provmaterial', header: 'Provmaterial', sortByField: 'provmaterial' },
      { field: 'ageBracket', header: 'Åldersintervall', sortByField: 'ageBracket' },
      { field: 'alderVidProvtagning', header: 'Ålder', sortByField: 'alderVidProvtagning' },
      { field: 'numberOfFynd', header: 'Antal Fynd' },
      { field: 'provtagningsDatum', header: 'Provtagningsdatum', sortByField: 'provtagningsDatum' },
      { field: 'gender', header: 'Kön', sortByField: 'gender', mapView: (gender) => dbStringToGui(gender) },
    ],
  };

  ngOnInit(): void {
    this.searchParams = JSON.parse(localStorage.getItem('searchParams') as any);
    this.searchParams.countSearch = ['PROV'];
    this.searchParams.offset = 0;
    this.initialSearchParams = structuredClone(this.searchParams);

    if (this.searchParams.searchType === SearchType.GENERAL) {
      this.tableConfig.columns?.splice(5, 0, {
        field: 'allSmittamne',
        header: 'Smittämne',
        type: 'array',
      });
    }

    if (this.searchParams.searchType === SearchType.NAMNARDATA) {
      this.tableConfig.columns?.push({
        field: 'fyndType',
        header: 'Typ av resultat',
        sortByField: 'fyndType',
        mapView: (x) => dbStringToGui(x),
      });
    }
    this.getCount();
    this.getKmlCount();
  }

  sort = (e: TableLazyLoadEvent) => {
    const sortBy = e.multiSortMeta?.map((sortObject: { field: string; order: number }) => {
      return { name: sortObject.field, order: sortObject.order === 1 ? 'ASC' : 'DESC' };
    });
    this.searchParams.offset = e.first;
    this.searchParams.sortBy = sortBy;

    // If filtering is to be implemented, use e.filter object to extract user input for filters

    this.searchAndGroupByProv();
  };

  private getCount() {
    this.analysresultatService
      .countSearch<any>(this.searchParams)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((count: any) => {
        this.totalRecords = count.provCount;
        this.countLoading = false;
      });
  }

  private getKmlCount() {
    this.analysresultatService
      .countKML<string[]>(this.searchParams)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((kmlList: string[]) => ((this.numberOfKML = kmlList.length), (this.kmlLoading = false)));
  }

  private searchAndGroupByProv() {
    switch (this.searchParams.searchType) {
      case SearchType.SPECIFIC:
      case SearchType.NAMNARDATA:
        return this.specificSearch();
      case SearchType.GENERAL:
        return this.generalSearch();

      default:
        break;
    }
  }

  private specificSearch() {
    this.loading = true;
    this.analysresultatService
      .searchGroupByProvId(this.searchParams)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((groupedResult: any) => {
        this.tableConfig.rows = this.mapGroupedResult(groupedResult);
        this.loading = false;
      });
  }

  private generalSearch() {
    this.loading = true;
    return this.analysresultatService
      .searchGeneralAndGroupByProvId(this.searchParams)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((groupedResult: any) => {
        this.tableConfig.rows = this.mapGroupedResult(groupedResult);
        this.tableConfig.rows?.forEach((row) => {
          row.allSmittamne = row.fynds.map((x: { name: string; id: string }) => x.name);
          this.loading = false;
        });
      });
  }

  private mapGroupedResult(result: any) {
    let rowData: any = [];
    for (const key in result) {
      result[key].forEach((entry: any, index: number) => {
        if (index === 0) {
          entry.numberOfFynd = result[key].length;
          if (this.searchParams.searchType === SearchType.SPECIFIC) {
            entry.fyndIds = result[key].map((x: any) => x.fyndId);
          }
          if (this.searchParams.searchType !== SearchType.SPECIFIC) {
            entry.fynds = result[key].map((x: any) => {
              return { name: x.smittamne, id: x.fyndId };
            });
          }
          rowData.push(entry);
        }
      });
    }

    return rowData;
  }

  private getAntibiotics(row: any) {
    this.currentRow = row;
    this.currentRow.fynd = [];
    if (this.searchParams.searchType === SearchType.NAMNARDATA) {
      this.currentRow.fynds.forEach((f: { name: string; id: string }) => {
        this.currentRow.fynd.push({ smittamne: f.name });
      });
      return;
    }
    if (this.currentRow.fynds?.length > 0) {
      this.currentRow.fynds.forEach((f: { name: string; id: string }) => {
        this.analysresultatService.getAntibiotics(f.id).subscribe((x) => {
          this.currentRow.fynd.push({ smittamne: f.name, antimicroList: x });
        });
      });
    } else {
      this.currentRow.fyndIds.forEach((id: string) => {
        this.analysresultatService.getAntibiotics(id).subscribe((x) => {
          this.currentRow.fynd.push({ smittamne: this.currentRow.smittamne, antimicroList: x });
        });
      });
    }
  }

  protected getRowDataCsv() {
    this.globalStateService.setCsvDownloading(true);

    if (this.searchParams.searchType !== SearchType.NAMNARDATA) {
      this.analysresultatService.getSpecificRowDataCsv<Blob>(this.searchParams).subscribe((tableData: Blob) => {
        saveAs(tableData, `SVEBAR_Provlista_${new Date().toLocaleString('sv-SE').replace(' ', 'T')}.csv`);
        this.globalStateService.setCsvDownloading(false);
      });
    }
    if (this.searchParams.searchType === SearchType.NAMNARDATA) {
      this.analysresultatService.getNamnarDataCsv<Blob>(this.searchParams).subscribe((tableData: Blob) => {
        saveAs(tableData, `SVEBAR_Nämnardata_Provlista_${new Date().toLocaleString('sv-SE').replace(' ', 'T')}.csv`);
        this.globalStateService.setCsvDownloading(false);
      });
    }
  }

  protected getSearchParams(params?: string[]) {
    return params?.join('\n');
  }

  protected formatBigNumber(number: number) {
    return !number ? '' : formatBigNumber(String(number));
  }
}
